import i18n from 'i18next';
import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appConfig, type ProductEntityType } from 'shared';
import { useOnlineManager } from '~/hooks/use-online-manager';
import { BlockNote } from '~/modules/common/blocknote/blocknote-editor';
import { UploadHostProvider } from '~/modules/common/blocknote/custom-file-panel/upload-host';
import { useYjsToken } from '~/modules/common/blocknote/hooks/use-yjs-token';
import { useYjsConnection } from '~/modules/common/blocknote/yjs-connections';
import { Spinner } from '~/modules/common/spinner';
import { toaster } from '~/modules/common/toaster/toaster';
import { useCurrentUser } from '~/modules/user/user-store';
import { getRandomColor } from '~/utils/random-color';

// BlockNote's props are a union (filePanel variants), so Omit must distribute over it
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type PassthroughProps = DistributiveOmit<
  ComponentProps<typeof BlockNote>,
  'collaboration' | 'defaultValue' | 'updateData' | 'onBeforeLoad' | 'id'
>;

type CollaborativeBlockNoteProps = PassthroughProps & {
  entityType: ProductEntityType;
  entityId: string;
  tenantId: string;
  organizationId: string;
  /** Unconditional update permission; collaboration only activates when it holds (the relay re-verifies). */
  canEdit: boolean;
  /** Stored description blocks (the entity row's source of truth outside a session). */
  description: string | null;
  /** Persistence policy; receives the live collaboration state per call. */
  updateData: (description: string, collaborative: boolean) => Promise<void> | void;
  /** Rendered while waiting for the token and the first WS sync (avoids an empty flash). Defaults to a spinner. */
  waitingFallback?: ReactNode;
};

/** How long the token fetch, connect and first sync may take before the editor opens standalone. */
const SYNC_TIMEOUT_MS = 5_000;

/**
 * BlockNote host for an entity description: owns the token, online and permission gates, the relay connection, and the
 * standalone fallback. A collaborative editor whose connection the relay ended for good turns read-only with a notice:
 * saving its document through the standalone path would overwrite the collaborators' latest edits it never received.
 */
export function CollaborativeBlockNote({
  entityType,
  entityId,
  tenantId,
  organizationId,
  canEdit,
  description,
  updateData,
  waitingFallback,
  ...blockNoteProps
}: CollaborativeBlockNoteProps) {
  const { t } = useTranslation();
  const user = useCurrentUser();

  const isOnline = useOnlineManager();
  const wantsCollaboration = appConfig.services.yjs.enabled && !!appConfig.yjsUrl && isOnline && canEdit;
  // The token names this entity only; the relay closes the socket when it expires, and the refreshed token reconnects it.
  const { token: yjsToken, refused } = useYjsToken({
    entityType,
    entityId,
    tenantId,
    organizationId,
    enabled: wantsCollaboration,
  });
  const canCollaborate = wantsCollaboration && !!yjsToken;

  // Once collaborative, hold the connection across an offline blip: releasing it lets the grace period destroy the shared doc under a mounted editor.
  const committedRef = useRef<'collab' | 'solo' | null>(null);
  const keepConnection = canCollaborate || committedRef.current === 'collab';

  // The token proves update permission; entity-level access is verified asynchronously by the relay.
  const yjsConn = useYjsConnection(keepConnection ? entityId : undefined, entityType, tenantId);
  const wsReady = yjsConn?.synced ?? false;

  // Wait briefly for the token and WS sync before falling back to standalone, so the editor is not mounted twice.
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const toastShownRef = useRef(false);
  useEffect(() => {
    if (!wantsCollaboration || refused || wsReady) return;
    const timer = setTimeout(() => {
      setSyncTimedOut(true);
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        toaster.warning(i18n.t('error:sync_failed.text'));
      }
    }, SYNC_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [wantsCollaboration, refused, wsReady]);

  // useCreateBlockNote captures the Yjs config at creation, so a solo/collab switch remounts the editor and discards
  // an open upload dialog and unsynced text. Commit the mode once per mount; reopening re-evaluates from scratch.
  const liveCollaborative = canCollaborate && wsReady;
  if (committedRef.current === null) {
    if (liveCollaborative) committedRef.current = 'collab';
    else if (!wantsCollaboration || refused || syncTimedOut) committedRef.current = 'solo';
  }
  const committed = committedRef.current;
  const waitingForSync = committed === null;
  const collaborative = committed === 'collab';
  // Nothing typed after the relay ended the session could reach it, so the editor stops taking edits.
  const collaborationStopped = collaborative && (yjsConn?.stopped ?? false);

  // Stable random color for cursor labels
  const userColorRef = useRef(getRandomColor());

  const collaborationBundle =
    collaborative && yjsConn
      ? {
          provider: yjsConn.provider,
          fragment: yjsConn.fragment,
          user: { name: user.name, color: userColorRef.current },
          entityType,
          entityId,
        }
      : undefined;

  // A reseeded document syncs afresh: the editor comes back on the new fragment once it did.
  const rebuilding = collaborative && !wsReady;
  if (waitingForSync || rebuilding) return waitingFallback ?? <Spinner className="my-8 h-6 w-6 opacity-50" />;

  const uploadHostProps = blockNoteProps.baseFilePanelProps;

  const editor = (
    <>
      {collaborationStopped && (
        <p role="status" className="mb-2 text-muted-foreground text-sm">
          {t('c:collaboration_stopped.text')}
        </p>
      )}
      <BlockNote
        // Stable for this mount, and new per rebuilt document; the key also guards against reusing a standalone editor
        // instance as collaborative.
        key={collaborative ? `collab-${yjsConn?.rebuilds ?? 0}` : 'solo'}
        id={`blocknote-${entityId}`}
        defaultValue={description ?? undefined}
        updateData={(blocks) => void updateData(blocks, collaborative)}
        collaboration={collaborationBundle}
        onBeforeLoad={
          collaborative
            ? undefined
            : (editor) => {
                const strBlocks = JSON.stringify(editor.document);
                if (description === null || strBlocks === description) return;
                void updateData(strBlocks, collaborative);
              }
        }
        {...blockNoteProps}
        editable={blockNoteProps.editable !== false && !collaborationStopped}
      />
    </>
  );

  // The upload dialog renders above the editor so it survives an editor remount.
  return uploadHostProps ? (
    <UploadHostProvider baseFilePanelProps={uploadHostProps}>{editor}</UploadHostProvider>
  ) : (
    editor
  );
}
